import { OverlayPage } from '@components/partials/OverlayPage';
import {
  DownloadIcon,
  Loader,
  CalendarIcon,
  ArrowLongRightIcon,
  ReportHeader,
} from '@cloudclevr/components';
import {
  useReportPdfUrlPreviewQuery,
  useReportPreviewQuery,
} from '@hooks/reports';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './styles.module.css';
import {
  ChartComponent,
  ReportChartData,
  ReportSelectedVendors,
} from '@hooks/reports/types';
import {} from '@pages/InsightsAndAnalytics/tiles/LineChartTile';
import { hasPermission, formatDateInUTC } from '@utils/index';
import { UserRoles, Permissions } from '@hooks/users/types';
import useAuthStore from '@store/authStore';
import RenderChartTile from './RenderChartTile';
import { ReportV2Preview } from './ReportV2Preview';
import type { ReportDocument } from '@cloudclevr/components';
import axios from 'axios';
import dayjs from 'dayjs';
import useUIStore from '@store/uiStore';
import { Variant } from '@components/ui/Toast';

/** AC014: format a date string as dd/mm/yy for use in footer and title page. */
function formatFooterDate(dateStr: string): string {
  const d = new Date(dateStr);
  return [
    String(d.getDate()).padStart(2, '0'),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getFullYear()).slice(-2),
  ].join('/');
}

function printWithFilename(
  reportTitle: string,
  fromDate?: string,
  toDate?: string
): void {
  // AC019/AC020: strip characters illegal in Windows filenames; fall back to 'Report' if empty.
  const sanitized = (reportTitle
    .replace(/[<>:"/\\|?*]/g, '-')
    .split('')
    .filter((ch) => ch.charCodeAt(0) > 31)
    .join('')
    .replace(/-+/g, '-')
    .trim()) || 'Report';

  const originalTitle = document.title;

  // Chrome "Save as PDF" uses document.title as the suggested filename.
  document.title = sanitized;

  // AC013/AC014: inject @page footer rules (report name + dates left, page number right).
  // AC015: suppress footer on title page. AC016: suppress footer on back page.
  const footerLeft = fromDate
    ? `${reportTitle}: ${formatFooterDate(fromDate)}${
        toDate ? ' - ' + formatFooterDate(toDate) : ''
      }`
    : reportTitle;
  const escapedLeft = footerLeft.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

  const styleEl = document.createElement('style');
  styleEl.id = 'pdf-running-footer';
  styleEl.textContent = `
    @page {
      @bottom-center { content: none; }
      @bottom-left {
        content: "${escapedLeft}";
        font-family: Arial, Helvetica, sans-serif;
        font-size: 8pt;
        color: #1e293b;
        border-top: 0.5pt solid #cbd5e1;
        padding-top: 2mm;
      }
      @bottom-right {
        content: counter(page);
        font-family: Arial, Helvetica, sans-serif;
        font-size: 9pt;
        font-weight: 700;
        color: #1e293b;
        border-top: 0.5pt solid #cbd5e1;
        padding-top: 2mm;
      }
    }
    @page back-page {
      @bottom-center { content: none; }
      @bottom-left   { content: none; border-top: none; }
      @bottom-right  { content: none; border-top: none; }
    }
  `;
  document.head.appendChild(styleEl);

  document.body.classList.add('pdf-printing');

  window.addEventListener('afterprint', () => {
    document.body.classList.remove('pdf-printing');
    document.title = originalTitle;
    const injected = document.getElementById('pdf-running-footer');
    if (injected) injected.remove();
  }, { once: true });

  // Wait for React to re-render with all table rows before opening print dialog
  // MutationObserver in TableRenderer detects pdf-printing class and triggers re-render
  setTimeout(() => {
    window.print();
  }, 100);
}

export const ReportPreviewPage: React.FC = () => { // NOSONAR
  const [selectedVendors, setSelectedVendors] = useState<ReportSelectedVendors>(
    {}
  );
  const [pdfUrl, setPdfUrl] = useState('');

  let chartData: ReportChartData | null = null;

  const { reportId } = useParams<{ reportId: string }>();

  const query = useReportPreviewQuery({
    reportId: reportId,
  });

  const { mutateAsync, isPending: isPdfUrlLoading } =
    useReportPdfUrlPreviewQuery(reportId);

  if (query.isFetched) {
    if (query.data?.jsonReportData) {
      chartData = JSON.parse(query.data?.jsonReportData);
    }
  }

  const isV2Report = chartData?.version === '2.0';

  const isV2ReportType = query.data?.type === 5;
  const reportTypeName = isV2ReportType
    ? (query.data?.title || query.data?.name || '').trim()
    : query.data?.name?.substring(12).trim() ?? '';
  const headerTitle = isV2Report && query.data?.organizationName
    ? `${query.data.organizationName}: ${reportTypeName}`
    : query.data?.name || '';

  const userRole = useAuthStore.getState().role || UserRoles.STANDARD;

  const downloadHasPermission = hasPermission(
    userRole,
    Permissions.ADMINS_SUPPORTUSER
  );

  useEffect(() => {
    if (query.isFetched && (!isV2Report || chartData?.hasPdf)) {
      getPdfUrl();
    }
  }, [query.isFetched, isV2Report]);

  const { addToast, setSidebar, overlayHeaderHeight } = useUIStore();
  const rendererContainerRef = useRef<HTMLDivElement>(null);

  // Handle Contents → ReportRenderer chapter/section navigation messages.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type !== 'REPORT_SCROLL_TO_ITEM') return;
      const { chapterIndex, sectionIndex } = event.data.payload ?? {};
      if (chapterIndex == null) return;

      const container = rendererContainerRef.current;
      if (!container) return;

      let target: Element | null = null;
      if (sectionIndex != null) {
        const chapterEl = container.querySelector(`#chapter-${chapterIndex}`);
        if (chapterEl) {
          target = chapterEl.querySelectorAll('[data-type="Section"]')[sectionIndex] ?? null;
        }
      } else {
        target = container.querySelector(`#chapter-${chapterIndex}`);
      }
      if (!target) return;

      const scrollEl =
        container.scrollHeight > container.clientHeight
          ? container
          : findScrollableAncestor(container);
      if (!scrollEl) return;

      const delta =
        (target as HTMLElement).getBoundingClientRect().top -
        scrollEl.getBoundingClientRect().top;

      scrollEl.scrollTo({ top: scrollEl.scrollTop + delta });
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    if (chartData && !isV2Report) {
      const initialVendors: ReportSelectedVendors = {};
      chartData.components.forEach((component: ChartComponent) => {
        initialVendors[component.type] = 'All';
      });
      setSelectedVendors(initialVendors);
    }
  }, [query.isFetched]);

  const getPdfUrl = async () => {
    if (downloadHasPermission) {
      const res = await mutateAsync();
      setPdfUrl(res as string);
    }
  };

  const downloadFile = async () => {
    try {
      const date = query.data?.name.substring(0, 11);
      const reportName = query.data?.name.substring(12).trim() ?? 'report';

      const name = reportName
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');

      const response = await axios({
        url: pdfUrl,
        method: 'GET',
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Clevr360_${name}_${dayjs(date, 'MMM DD YYYY').format(
          'DD-MMM-YYYY'
        )}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      addToast({
        variant: Variant.Error,
        message: `Failed to download the file: ${error}`,
      });
    }
  };

  return (
    <OverlayPage
      isFooterVisible={false}
      path="/reports"
      headerTitle={headerTitle}
      isLoading={query.isLoading}
      overlayClassNames={isV2Report ? ' !pl-10 pdf-ui-only' : ' !pl-10'}
      contentClassNames={isV2Report ? '!p-0 !m-0 flex-1 min-h-0 flex flex-col pdf-v2-print-content' : '!pt-0'}
    >
      {query.isLoading ? (
        <Loader />
      ) : isV2Report && chartData ? (
        <>
          {/* Pinned header bar for V2 — always visible (print button doesn't depend on hasPdf) */}
          {(isV2Report || chartData?.hasPdf || query.data?.createdAt || chartData?.fromDate) && (
            <div className="pdf-ui-only flex-shrink-0 bg-interfaceColor-5 border-b border-gray-200 pl-2 pb-3 pt-2 ml-1">
              <div className="pl-5">
              <ReportHeader
                fromDate={chartData?.fromDate}
                toDate={chartData?.toDate}
                publishedDate={query.data?.createdAt}
                onDownload={() => printWithFilename(reportTypeName, chartData?.fromDate, chartData?.toDate)}
              />
              </div>
            </div>
          )}
          {/* Contents + ReportRenderer — only the renderer scrolls */}          
          <ReportV2Preview
            document={chartData as unknown as ReportDocument}
            rendererRef={rendererContainerRef}
          />
        </>
      ) : (
        <>
          {/* Only show ReportHeader if hasPdf is true or publishedDate is not null */}
          {(chartData?.hasPdf || query.data?.createdAt) && (
            <div
              className="sticky z-10 bg-interfaceColor-5 pl-2 pb-2 mb-3"
              style={{ top: overlayHeaderHeight || 0 }}
            >
              <ReportHeader
                publishedDate={query.data?.createdAt}
                onDownload={chartData?.hasPdf && downloadHasPermission ? downloadFile : undefined}
              />
            </div>
          )}
          {chartData?.fromDate !== undefined &&
            chartData?.toDate !== undefined && (
              <h4
                className="mb-4 flex items-center gap-x-1"
                style={{
                  fontSize: '16px',
                  breakInside: 'avoid',
                }}
              >
                <CalendarIcon
                  color="text-interfaceColor-100"
                  size="6"
                  classNames="ml-1"
                />
                {formatDateInUTC(chartData?.fromDate)}
                <ArrowLongRightIcon color="text-interfaceColor-100" size="5" />
                {formatDateInUTC(chartData?.toDate)}
              </h4>
            )}
          {downloadHasPermission && !isPdfUrlLoading && (
            <button
              className="inline-flex flex-shrink-0 items-center justify-center rounded-[24px] bg-interfaceColor-90 px-[16px] py-[10px] text-16 font-normal leading-6 tracking-[0.01em] text-white transition duration-300 ease-in-out hover:bg-interfaceColor-150 hover:text-interfaceColor-10 focus:shadow-focus disabled:bg-interfaceColor-20 disabled:text-interfaceColor-80"
              onClick={() => downloadFile()}
            >
              {' '}
              <DownloadIcon
                classNames="mr-2"
                color="text-primaryBrandColor-150"
              />
              Download report
            </button>
          )}
          {query.isFetched && chartData && !query.isLoading && (
            <div className={styles.pageGrid}>
              {chartData.components.map((item) => {
                return (
                  <RenderChartTile
                    key={item.type}
                    item={item}
                    selectedVendor={selectedVendors}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </OverlayPage>
  );
};

/** Walk up the DOM to find the nearest element that actually scrolls. */
function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent && parent !== document.documentElement) {
    const style = getComputedStyle(parent);
    const overflowY = style.overflowY || style.overflow;
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document.documentElement as HTMLElement;
}
